import {
  ATTN,
  ATTN_TEMPLATE,
  CLEN,
  CLEN_TEMPLATE,
  ENRL,
  ENRL_TEMPLATE,
  GlobalStateModel,
  LANG,
  LANG_TEMPLATE,
  PRGM,
  PRGM_TEMPLATE,
  SLNG,
  SLNG_TEMPLATE,
} from "@/models";
import { SearchResult } from "@/models";
import { CHLD, CHLD_TEMPLATE } from "@/models/chld.tsx";
import { FAMI, FAMI_TEMPLATE } from "@/models/fami.tsx";
import { fetchAuthenticatedData } from "@/queries/shared.ts";
import { useRouteContext } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { ChildSearchFormState } from "../ChildSearchForm";

type LockedFields = {
  csppid: boolean;
  cicn: boolean;
  firstName: boolean;
  lastName: boolean;
  dob: boolean;
  preschool: boolean;
};

type FormConfig = {
  form: UseFormReturn<any>;
  template: any;
  skipFlag: boolean;
  setSkipFlag: (skip: boolean) => void;
  includeCSPPID?: boolean;
  includeEffectiveStartDate?: boolean;
};

type StepConfig = {
  skipFlag: boolean;
  formToValidate: UseFormReturn<any>;
  template: any;
  nextFormsToPrepopulate?: Array<{
    form: UseFormReturn<any>;
    skipFlag: boolean;
    includeCSPPID?: boolean;
    includeEffectiveStartDate?: boolean;
  }>;
};

export const MAX_CLASSROOM_ENROLLMENTS = 5;
export const MAX_ATTENDANCE_RECORDS = 12;
export const MAX_PRGM_RECORDS = 5;

const UNSET_ETHNICITY = "__UNSET__";
const convertEthnicity = (value: any, missingIndicator: any): string => {
  if (value === true || value === "Y") return "Y";
  if (value === false || value === "N") return "N";
  if (missingIndicator === true || missingIndicator === "Y") return "";
  return UNSET_ETHNICITY;
};

const convertMissingIndicator = (value: any): string => {
  if (value === true || value === "Y") return "Y";
  if (value === false || value === "N") return "N";
  return "Y";
};

export const PRE_POPULATED_FIELDS = new Set([
  "CSPPID",
  "LocalChildId",
  "PreschoolCode",
  "ProgramYear",
  "EffectiveStartDate",
  "ReportingLEA",
]);

const formHasData = (
  formInstance: UseFormReturn<any>,
  template: any
): boolean => {
  const values = formInstance.getValues();
  const templateKeys = Object.keys(template);

  return templateKeys.some((key) => {
    if (PRE_POPULATED_FIELDS.has(key)) return false;
    const currentVal = values[key];
    const templateVal = template[key];
    return (
      currentVal !== templateVal &&
      currentVal !== "" &&
      currentVal !== undefined &&
      currentVal !== null
    );
  });
};

export function useAddChildWizard() {
  const leaContext = useRouteContext({ from: "/$leaid" }) as GlobalStateModel;

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([1]);
  const [searchResults, setSearchResults] = useState<SearchResult<CHLD>[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searchedData, setSearchedData] = useState({ name: "", dob: "" });
  const [searchCriteria, setSearchCriteria] =
    useState<ChildSearchFormState | null>(null);
  const [isNewRecord, setIsNewRecord] = useState(false);
  const [lockedFields, setLockedFields] = useState<LockedFields>({
    csppid: false,
    cicn: false,
    firstName: false,
    lastName: false,
    dob: false,
    preschool: false,
  });
  const [searchError, setSearchError] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);
  const [hasReachedReview, setHasReachedReview] = useState(false);

  const [skipFamily, setSkipFamily] = useState(false);
  const [skipLanguage, setSkipLanguage] = useState(false);
  const [skipSupplementalLanguage, setSkipSupplementalLanguage] =
    useState(false);
  const [skipEnrollment, setSkipEnrollment] = useState(false);
  const [skipClassroomEnrollment, setSkipClassroomEnrollment] = useState(false);
  const [skipAttendance, setSkipAttendance] = useState(false);
  const [skipPrgm, setSkipPrgm] = useState(false);

  const form = useForm<CHLD>({
    defaultValues: {
      ...CHLD_TEMPLATE,
      ChildEthnicity: UNSET_ETHNICITY,
    },
    mode: "onChange",
  });

  const familyForm = useForm<FAMI>({
    defaultValues: FAMI_TEMPLATE,
    mode: "onChange",
  });

  const languageForm = useForm<LANG>({
    defaultValues: LANG_TEMPLATE,
    mode: "onChange",
  });

  const supplementalLanguageForm = useForm<SLNG>({
    defaultValues: SLNG_TEMPLATE,
    mode: "onChange",
  });

  const enrollmentForm = useForm<ENRL>({
    defaultValues: ENRL_TEMPLATE,
    mode: "onChange",
  });

  const classroomEnrollmentForm = useForm<CLEN>({
    defaultValues: CLEN_TEMPLATE,
    mode: "onChange",
  });

  const classroomEnrollmentForm2 = useForm<CLEN>({
    defaultValues: CLEN_TEMPLATE,
    mode: "onChange",
  });

  const classroomEnrollmentForm3 = useForm<CLEN>({
    defaultValues: CLEN_TEMPLATE,
    mode: "onChange",
  });

  const classroomEnrollmentForm4 = useForm<CLEN>({
    defaultValues: CLEN_TEMPLATE,
    mode: "onChange",
  });

  const classroomEnrollmentForm5 = useForm<CLEN>({
    defaultValues: CLEN_TEMPLATE,
    mode: "onChange",
  });

  const classroomEnrollmentForms = [
    classroomEnrollmentForm,
    classroomEnrollmentForm2,
    classroomEnrollmentForm3,
    classroomEnrollmentForm4,
    classroomEnrollmentForm5,
  ];

  const [activeClassroomCount, setActiveClassroomCount] = useState(1);

  const attendanceForm1 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm2 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm3 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm4 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm5 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm6 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm7 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm8 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm9 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm10 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm11 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });
  const attendanceForm12 = useForm<ATTN>({
    defaultValues: ATTN_TEMPLATE,
    mode: "onChange",
  });

  const attendanceForms = [
    attendanceForm1,
    attendanceForm2,
    attendanceForm3,
    attendanceForm4,
    attendanceForm5,
    attendanceForm6,
    attendanceForm7,
    attendanceForm8,
    attendanceForm9,
    attendanceForm10,
    attendanceForm11,
    attendanceForm12,
  ];

  const [activeAttendanceCount, setActiveAttendanceCount] = useState(1);

  const [attendanceMonthsInUse, setAttendanceMonthsInUse] = useState<string[]>(
    new Array(MAX_ATTENDANCE_RECORDS).fill("")
  );

  const prgmForm1 = useForm<PRGM>({
    defaultValues: PRGM_TEMPLATE,
    mode: "onChange",
  });
  const prgmForm2 = useForm<PRGM>({
    defaultValues: PRGM_TEMPLATE,
    mode: "onChange",
  });
  const prgmForm3 = useForm<PRGM>({
    defaultValues: PRGM_TEMPLATE,
    mode: "onChange",
  });
  const prgmForm4 = useForm<PRGM>({
    defaultValues: PRGM_TEMPLATE,
    mode: "onChange",
  });
  const prgmForm5 = useForm<PRGM>({
    defaultValues: PRGM_TEMPLATE,
    mode: "onChange",
  });

  const prgmForms = [prgmForm1, prgmForm2, prgmForm3, prgmForm4, prgmForm5];

  const [activePrgmCount, setActivePrgmCount] = useState(1);

  useEffect(() => {
    const subscriptions = attendanceForms.map((f, i) =>
      f.watch((_values, { name }) => {
        if (name !== "FirstOfMonth" && name !== undefined) return;
        const val = f.getValues("FirstOfMonth") || "";
        setAttendanceMonthsInUse((prev) => {
          if (prev[i] === val) return prev;
          const next = [...prev];
          next[i] = val;
          return next;
        });
      })
    );
    return () => subscriptions.forEach((s) => s.unsubscribe());
    // attendanceForms's individual form instances are stable across renders
    // (react-hook-form memoizes useForm's return value), so re-subscribing
    // when the wrapping array's reference changes is harmless.
  }, [attendanceForms]);

  const canCreateNew = !!(
    searchCriteria?.firstName &&
    searchCriteria?.lastName &&
    searchCriteria?.dateOfBirth
  );

  const prePopulateForm = (
    targetForm: UseFormReturn<any>,
    includeCSPPID: boolean = true,
    includeEffectiveStartDate: boolean = false
  ) => {
    const childData = form.getValues();
    const currentData = targetForm.getValues();

    if (includeCSPPID && !isNewRecord && !currentData.CSPPID) {
      targetForm.setValue("CSPPID", childData.CSPPID || "");
    }
    if (!currentData.LocalChildId) {
      targetForm.setValue("LocalChildId", childData.LocalChildId || "");
    }
    targetForm.setValue("PreschoolCode", childData.PreschoolCode || "");
    if (!currentData.ProgramYear) {
      targetForm.setValue("ProgramYear", childData.ProgramYear || "");
    }
    if (includeEffectiveStartDate && !currentData.EffectiveStartDate) {
      targetForm.setValue(
        "EffectiveStartDate",
        childData.EffectiveStartDate || ""
      );
    }
  };

  const prePopulatePrgmForm = (targetForm: UseFormReturn<PRGM>) => {
    prePopulateForm(targetForm, true, false);
    if (!targetForm.getValues().ReportingLEA) {
      targetForm.setValue(
        "ReportingLEA",
        leaContext.currentUserOrg?.cdsCode || ""
      );
    }
  };

  const prePopulateAllFormsFromCHLD = () => {
    prePopulateForm(familyForm, true, false);
    prePopulateForm(languageForm, true, true);
    prePopulateForm(supplementalLanguageForm, true, true);
    prePopulateForm(enrollmentForm, true, false);
    for (let i = 0; i < activeClassroomCount; i++) {
      prePopulateForm(classroomEnrollmentForms[i], true, false);
    }
    for (let i = 0; i < activeAttendanceCount; i++) {
      prePopulateForm(attendanceForms[i], true, false);
    }
    for (let i = 0; i < activePrgmCount; i++) {
      prePopulatePrgmForm(prgmForms[i]);
    }
  };

  const createSkipHandler = (config: FormConfig) => {
    return (skip: boolean) => {
      config.setSkipFlag(skip);
      if (skip) {
        config.form.reset(config.template);
      } else {
        prePopulateForm(
          config.form,
          config.includeCSPPID,
          config.includeEffectiveStartDate
        );
      }
    };
  };

  const addClassroomEnrollment = () => {
    if (activeClassroomCount >= MAX_CLASSROOM_ENROLLMENTS) return;
    const newIndex = activeClassroomCount;
    classroomEnrollmentForms[newIndex].reset(CLEN_TEMPLATE);
    prePopulateForm(classroomEnrollmentForms[newIndex], true, false);
    setActiveClassroomCount((c) => c + 1);
  };

  const removeClassroomEnrollment = (index: number) => {
    if (index <= 0 || index >= activeClassroomCount) return;

    for (let i = index; i < activeClassroomCount - 1; i++) {
      classroomEnrollmentForms[i].reset(
        classroomEnrollmentForms[i + 1].getValues()
      );
    }
    classroomEnrollmentForms[activeClassroomCount - 1].reset(CLEN_TEMPLATE);
    setActiveClassroomCount((c) => c - 1);
  };

  const addAttendanceRecord = () => {
    if (activeAttendanceCount >= MAX_ATTENDANCE_RECORDS) return;
    const newIndex = activeAttendanceCount;
    attendanceForms[newIndex].reset(ATTN_TEMPLATE);
    prePopulateForm(attendanceForms[newIndex], true, false);
    setActiveAttendanceCount((c) => c + 1);
  };

  const removeAttendanceRecord = (index: number) => {
    if (index <= 0 || index >= activeAttendanceCount) return;

    for (let i = index; i < activeAttendanceCount - 1; i++) {
      attendanceForms[i].reset(attendanceForms[i + 1].getValues());
    }
    attendanceForms[activeAttendanceCount - 1].reset(ATTN_TEMPLATE);
    setActiveAttendanceCount((c) => c - 1);
  };

  const addPrgmRecord = () => {
    if (activePrgmCount >= MAX_PRGM_RECORDS) return;
    const newIndex = activePrgmCount;
    prgmForms[newIndex].reset(PRGM_TEMPLATE);
    prePopulatePrgmForm(prgmForms[newIndex]);
    setActivePrgmCount((c) => c + 1);
  };

  const removePrgmRecord = (index: number) => {
    if (index <= 0 || index >= activePrgmCount) return;

    for (let i = index; i < activePrgmCount - 1; i++) {
      prgmForms[i].reset(prgmForms[i + 1].getValues());
    }
    prgmForms[activePrgmCount - 1].reset(PRGM_TEMPLATE);
    setActivePrgmCount((c) => c - 1);
  };

  const handleSearch = async (filters: ChildSearchFormState) => {
    const name = [filters.firstName, filters.lastName]
      .filter(Boolean)
      .join(" ");

    setSearchedData({
      name: name,
      dob: filters.dateOfBirth,
    });
    setSearchCriteria(filters);

    const csppid = filters.csppid?.trim().toLowerCase();

    const dob = filters.dateOfBirth?.trim().toLowerCase();

    const isCSPPIDSearch = !!csppid;

    setIsSearching(true);
    setSearchError("");

    try {
      const results = await fetchAuthenticatedData<SearchResult<CHLD>[]>(
        leaContext,
        `/json/child/search`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            CSPPID: filters.csppid || "",
            ChildFirstName: filters.firstName || "",
            ChildLastName: filters.lastName || "",
            ChildDateOfBirth: dob || "2026-01-01",
          }),
        }
      );
      setSearchResults(results);

      if (isCSPPIDSearch) {
        if (results.length === 0) {
          setSearchError(
            `No matches found for CSPPID "${filters.csppid}". Please search again using First Name, Last Name, and Date of Birth.`
          );
          setShowResults(false);
          setSearchCriteria({
            csppid: "",
            firstName: "",
            lastName: "",
            dateOfBirth: "",
          });
        } else if (results.length === 1) {
          setSearchError("");
          handleSelectChild(results[0].matchRecord);
        } else {
          setSearchError("");
          setShowResults(true);
        }
      } else {
        setSearchError("");
        setShowResults(true);
      }
    } catch (err) {
      setSearchResults([]);
      setShowResults(false);
      setSearchError(
        "Something went wrong while searching. Please try again. If the problem keeps happening, contact support."
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectChild = (child: CHLD) => {
    setIsNewRecord(false);

    const currentOrgPreschoolCodes = new Set(
      leaContext.currentOrgPreschools.map((p) => p.PreschoolCDSCode)
    );
    const isFromCurrentLea = currentOrgPreschoolCodes.has(
      child.PreschoolCode || ""
    );

    const normalizedChild: CHLD = {
      ...child,
      ChildEthnicity: convertEthnicity(
        child.ChildEthnicity,
        child.ChildEthnicityMissingIndicator
      ),
      ChildEthnicityMissingIndicator: convertMissingIndicator(
        child.ChildEthnicityMissingIndicator
      ),
      ChildRaceMissingIndicator: convertMissingIndicator(
        child.ChildRaceMissingIndicator
      ),
      ChildRace1: child.ChildRace1 ?? "",
      ChildRace2: child.ChildRace2 ?? "",
      ChildRace3: child.ChildRace3 ?? "",
      ChildRace4: child.ChildRace4 ?? "",
      ChildRace5: child.ChildRace5 ?? "",
    };

    if (isFromCurrentLea) {
      setLockedFields({
        csppid: true,
        cicn: true,
        firstName: false,
        lastName: false,
        dob: false,
        preschool: true,
      });
      form.reset(normalizedChild);
    } else {
      setLockedFields({
        csppid: true,
        cicn: false,
        firstName: false,
        lastName: false,
        dob: false,
        preschool: false,
      });
      form.reset({
        ...normalizedChild,
        LocalChildId: "",
        PreschoolCode: "",
      });
    }

    setCurrentStep(2);

    setCompletedSteps((prev) => {
      const newCompleted = new Set([...prev, 2]);
      return Array.from(newCompleted).sort((a, b) => a - b);
    });
  };

  const handleCreateNew = () => {
    if (
      !searchCriteria?.firstName ||
      !searchCriteria?.lastName ||
      !searchCriteria?.dateOfBirth
    ) {
      return;
    }

    setIsNewRecord(true);
    const newChildData = {
      ...CHLD_TEMPLATE,
      ChildFirstName: searchCriteria.firstName,
      ChildLastName: searchCriteria.lastName,
      ChildDateOfBirth: searchCriteria.dateOfBirth,
      CSPPID: "",
      ChildEthnicity: UNSET_ETHNICITY,
    };
    form.reset(newChildData);
    setLockedFields({
      csppid: true,
      cicn: false,
      firstName: true,
      lastName: true,
      dob: true,
      preschool: false,
    });
    setCurrentStep(2);

    setCompletedSteps((prev) => {
      const newCompleted = new Set([...prev, 2]);
      return Array.from(newCompleted).sort((a, b) => a - b);
    });
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setCompletedSteps([1]);
    setSearchResults([]);
    setShowResults(false);
    setSearchedData({ name: "", dob: "" });
    setSearchCriteria(null);
    setIsNewRecord(false);
    setLockedFields({
      csppid: false,
      cicn: false,
      firstName: false,
      lastName: false,
      dob: false,
      preschool: false,
    });
    setSearchError("");
    setIsSearching(false);
    setHasReachedReview(false);
    setSkipFamily(false);
    setSkipLanguage(false);
    setSkipSupplementalLanguage(false);
    setSkipEnrollment(false);
    setSkipClassroomEnrollment(false);
    setSkipAttendance(false);
    setSkipPrgm(false);
    form.reset({
      ...CHLD_TEMPLATE,
      ChildEthnicity: UNSET_ETHNICITY,
    });
    familyForm.reset(FAMI_TEMPLATE);
    languageForm.reset(LANG_TEMPLATE);
    supplementalLanguageForm.reset(SLNG_TEMPLATE);
    enrollmentForm.reset(ENRL_TEMPLATE);
    classroomEnrollmentForms.forEach((f) => f.reset(CLEN_TEMPLATE));
    setActiveClassroomCount(1);
    attendanceForms.forEach((f) => f.reset(ATTN_TEMPLATE));
    setActiveAttendanceCount(1);
    prgmForms.forEach((f) => f.reset(PRGM_TEMPLATE));
    setActivePrgmCount(1);
  };

  const handleNext = async () => {
    let isValid = false;

    const stepConfig: Record<number, StepConfig> = {
      2: {
        skipFlag: false,
        formToValidate: form,
        template: CHLD_TEMPLATE,
      },
      3: {
        skipFlag: skipFamily,
        formToValidate: familyForm,
        template: FAMI_TEMPLATE,
      },
      4: {
        skipFlag: skipLanguage,
        formToValidate: languageForm,
        template: LANG_TEMPLATE,
      },
      5: {
        skipFlag: skipSupplementalLanguage,
        formToValidate: supplementalLanguageForm,
        template: SLNG_TEMPLATE,
      },
      6: {
        skipFlag: skipEnrollment,
        formToValidate: enrollmentForm,
        template: ENRL_TEMPLATE,
      },
    };

    const config = stepConfig[currentStep];

    if (currentStep === 7) {
      if (skipClassroomEnrollment) {
        classroomEnrollmentForms.forEach((f) => f.reset(CLEN_TEMPLATE));
        isValid = true;
      } else {
        const results = await Promise.all(
          classroomEnrollmentForms
            .slice(0, activeClassroomCount)
            .map((f) => f.trigger())
        );
        isValid = results.every(Boolean);
      }
    } else if (config) {
      if (config.skipFlag) {
        config.formToValidate.reset(config.template);
        isValid = true;
      } else {
        isValid = await config.formToValidate.trigger();
      }

      if (isValid && currentStep === 2) {
        prePopulateAllFormsFromCHLD();
      }
    } else {
      isValid = true;
    }

    if (isValid) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);

      if (nextStep === 8) {
        setHasReachedReview(true);
      }

      setCompletedSteps((prev) => {
        const newCompleted = new Set([...prev, currentStep, nextStep]);
        return Array.from(newCompleted).sort((a, b) => a - b);
      });
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setSearchError("");
      setCurrentStep(1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const clearSearchError = () => {
    setSearchError("");
  };

  const handleSkipFamily = createSkipHandler({
    form: familyForm,
    template: FAMI_TEMPLATE,
    skipFlag: skipFamily,
    setSkipFlag: setSkipFamily,
    includeCSPPID: true,
    includeEffectiveStartDate: false,
  });

  const handleSkipLanguage = createSkipHandler({
    form: languageForm,
    template: LANG_TEMPLATE,
    skipFlag: skipLanguage,
    setSkipFlag: setSkipLanguage,
    includeCSPPID: true,
    includeEffectiveStartDate: true,
  });

  const handleSkipSupplementalLanguage = createSkipHandler({
    form: supplementalLanguageForm,
    template: SLNG_TEMPLATE,
    skipFlag: skipSupplementalLanguage,
    setSkipFlag: setSkipSupplementalLanguage,
    includeCSPPID: true,
    includeEffectiveStartDate: true,
  });

  const handleSkipEnrollment = createSkipHandler({
    form: enrollmentForm,
    template: ENRL_TEMPLATE,
    skipFlag: skipEnrollment,
    setSkipFlag: setSkipEnrollment,
    includeCSPPID: true,
    includeEffectiveStartDate: false,
  });

  const handleSkipClassroomEnrollment = (skip: boolean) => {
    setSkipClassroomEnrollment(skip);
    if (skip) {
      classroomEnrollmentForms.forEach((f) => f.reset(CLEN_TEMPLATE));
      setActiveClassroomCount(1);
    } else {
      prePopulateForm(classroomEnrollmentForms[0], true, false);
    }
  };

  const handleSkipAttendance = (skip: boolean) => {
    setSkipAttendance(skip);
    if (skip) {
      attendanceForms.forEach((f) => f.reset(ATTN_TEMPLATE));
      setActiveAttendanceCount(1);
    } else {
      prePopulateForm(attendanceForms[0], true, false);
    }
  };

  const handleSkipPrgm = (skip: boolean) => {
    setSkipPrgm(skip);
    if (skip) {
      prgmForms.forEach((f) => f.reset(PRGM_TEMPLATE));
      setActivePrgmCount(1);
    } else {
      prePopulatePrgmForm(prgmForms[0]);
    }
  };

  const skipToReview = async () => {
    const optionalSteps: Array<{
      step: number;
      form: UseFormReturn<any>;
      template: any;
      skipFlag: boolean;
      setSkip: (v: boolean) => void;
    }> = [
      {
        step: 3,
        form: familyForm,
        template: FAMI_TEMPLATE,
        skipFlag: skipFamily,
        setSkip: setSkipFamily,
      },
      {
        step: 4,
        form: languageForm,
        template: LANG_TEMPLATE,
        skipFlag: skipLanguage,
        setSkip: setSkipLanguage,
      },
      {
        step: 5,
        form: supplementalLanguageForm,
        template: SLNG_TEMPLATE,
        skipFlag: skipSupplementalLanguage,
        setSkip: setSkipSupplementalLanguage,
      },
      {
        step: 6,
        form: enrollmentForm,
        template: ENRL_TEMPLATE,
        skipFlag: skipEnrollment,
        setSkip: setSkipEnrollment,
      },
    ];

    const anyClassroomHasData = () =>
      classroomEnrollmentForms
        .slice(0, activeClassroomCount)
        .some((f) => formHasData(f, CLEN_TEMPLATE));

    const validateActiveClassrooms = async () => {
      const results = await Promise.all(
        classroomEnrollmentForms
          .slice(0, activeClassroomCount)
          .map((f) => f.trigger())
      );
      return results.every(Boolean);
    };

    const anyAttendanceHasData = () =>
      attendanceForms
        .slice(0, activeAttendanceCount)
        .some((f) => formHasData(f, ATTN_TEMPLATE));

    const validateActiveAttendance = async () => {
      const results = await Promise.all(
        attendanceForms.slice(0, activeAttendanceCount).map((f) => f.trigger())
      );
      return results.every(Boolean);
    };

    const anyPrgmHasData = () =>
      prgmForms
        .slice(0, activePrgmCount)
        .some((f) => formHasData(f, PRGM_TEMPLATE));

    const validateActivePrgm = async () => {
      const results = await Promise.all(
        prgmForms.slice(0, activePrgmCount).map((f) => f.trigger())
      );
      return results.every(Boolean);
    };

    if (hasReachedReview) {
      if (currentStep === 2) {
        const isValid = await form.trigger();
        if (!isValid) return;
      }

      if (currentStep >= 3 && currentStep <= 6) {
        const current = optionalSteps.find((s) => s.step === currentStep);
        if (current && !current.skipFlag) {
          const isValid = await current.form.trigger();
          if (!isValid) return;
        }
      }

      if (currentStep === 7 && !skipClassroomEnrollment) {
        const isValid = await validateActiveClassrooms();
        if (!isValid) return;
      }

      if (currentStep === 8 && !skipAttendance) {
        const isValid = await validateActiveAttendance();
        if (!isValid) return;
      }

      if (currentStep === 9 && !skipPrgm) {
        const isValid = await validateActivePrgm();
        if (!isValid) return;
      }

      setCurrentStep(10);
      setCompletedSteps((prev) => {
        const newCompleted = new Set([...prev, 10]);
        return Array.from(newCompleted).sort((a, b) => a - b);
      });
      return;
    }

    if (currentStep === 2) {
      const isValid = await form.trigger();
      if (!isValid) return;
      prePopulateAllFormsFromCHLD();
    }

    if (currentStep >= 3 && currentStep <= 6) {
      const current = optionalSteps.find((s) => s.step === currentStep);
      if (current && !current.skipFlag) {
        if (formHasData(current.form, current.template)) {
          const isValid = await current.form.trigger();
          if (!isValid) return;
        } else {
          current.setSkip(true);
          current.form.reset(current.template);
        }
      }
    }

    if (currentStep === 7 && !skipClassroomEnrollment) {
      if (anyClassroomHasData()) {
        const isValid = await validateActiveClassrooms();
        if (!isValid) return;
      } else {
        setSkipClassroomEnrollment(true);
        classroomEnrollmentForms.forEach((f) => f.reset(CLEN_TEMPLATE));
        setActiveClassroomCount(1);
      }
    }

    if (currentStep === 8 && !skipAttendance) {
      if (anyAttendanceHasData()) {
        const isValid = await validateActiveAttendance();
        if (!isValid) return;
      } else {
        setSkipAttendance(true);
        attendanceForms.forEach((f) => f.reset(ATTN_TEMPLATE));
        setActiveAttendanceCount(1);
      }
    }

    if (currentStep === 9 && !skipPrgm) {
      if (anyPrgmHasData()) {
        const isValid = await validateActivePrgm();
        if (!isValid) return;
      } else {
        setSkipPrgm(true);
        prgmForms.forEach((f) => f.reset(PRGM_TEMPLATE));
        setActivePrgmCount(1);
      }
    }

    for (const step of optionalSteps) {
      if (step.step <= currentStep) continue;
      if (step.skipFlag) continue;

      if (!formHasData(step.form, step.template)) {
        step.setSkip(true);
        step.form.reset(step.template);
      }
    }

    if (currentStep < 7 && !skipClassroomEnrollment) {
      if (!anyClassroomHasData()) {
        setSkipClassroomEnrollment(true);
        classroomEnrollmentForms.forEach((f) => f.reset(CLEN_TEMPLATE));
        setActiveClassroomCount(1);
      }
    }

    if (currentStep < 8 && !skipAttendance) {
      if (!anyAttendanceHasData()) {
        setSkipAttendance(true);
        attendanceForms.forEach((f) => f.reset(ATTN_TEMPLATE));
        setActiveAttendanceCount(1);
      }
    }

    if (currentStep < 9 && !skipPrgm) {
      if (!anyPrgmHasData()) {
        setSkipPrgm(true);
        prgmForms.forEach((f) => f.reset(PRGM_TEMPLATE));
        setActivePrgmCount(1);
      }
    }

    setHasReachedReview(true);
    setCurrentStep(10);
    setCompletedSteps((prev) => {
      const newCompleted = new Set([
        ...prev,
        ...Array.from({ length: 10 }, (_, i) => i + 1),
      ]);
      return Array.from(newCompleted).sort((a, b) => a - b);
    });
  };

  return {
    currentStep,
    completedSteps,
    searchResults,
    showResults,
    searchedData,
    isNewRecord,
    lockedFields,
    form,
    familyForm,
    languageForm,
    supplementalLanguageForm,
    enrollmentForm,
    classroomEnrollmentForms,
    activeClassroomCount,
    attendanceForms,
    activeAttendanceCount,
    attendanceMonthsInUse,
    prgmForms,
    activePrgmCount,
    canCreateNew,
    searchError,
    isSearching,
    searchCriteria,
    skipFamily,
    skipLanguage,
    skipSupplementalLanguage,
    skipEnrollment,
    skipClassroomEnrollment,
    skipAttendance,
    skipPrgm,
    hasReachedReview,

    handleSearch,
    handleSelectChild,
    handleCreateNew,
    handleNext,
    handleBack,
    resetWizard,
    setCurrentStep,
    setCompletedSteps,
    clearSearchError,
    handleSkipFamily,
    handleSkipLanguage,
    handleSkipSupplementalLanguage,
    handleSkipEnrollment,
    handleSkipClassroomEnrollment,
    handleSkipAttendance,
    handleSkipPrgm,
    addClassroomEnrollment,
    addAttendanceRecord,
    removeAttendanceRecord,
    removeClassroomEnrollment,
    addPrgmRecord,
    removePrgmRecord,
    skipToReview,
    prePopulateAllFormsFromCHLD,
  };
}
